import Footer from 'components/Footer/Footer';
import { H2, SubtitleM, GRAY60 } from 'global-styles';
import { useRouter } from 'next/router';
import { memo, ReactNode, useMemo } from 'react';
import styled from 'styled-components';
import ProductBanner from './ProductBanner';
import ProductTabMenu from './ProductTabMenu';
import ProductDescription from './ProductDescription';
import { gridSystemMargin } from 'components/Layout';
import ProductFeatures from './ProductFeatures';
import ProductDevicesPlatforms from './ProductDevicesPlatforms';
import ProductProvide from './ProductProvide';
import ProductContactUs from './ProductContactUs';
import { txtAnimation } from 'components/Home/BannerItems';
import { ProductTemplateButtons } from 'locale/ProductTemplateButtons';
import AvifProductFeatures from './Pulsar/AvifProductFeatures';
import { useInView } from 'react-intersection-observer';
import {
  deepfieldContents,
  pulsarContents,
} from 'locale/productTemplateContents';

export interface TabItem {
  label: string;
  pathname:
    | '/product/sr'
    | '/product/pqo'
    | '/product/avif'
    | '/product/av1'
    | '/product/vmaf';
}

interface Props {
  productName: 'Pulsar' | 'DeepField';
  productDescription: ReactNode[]; // en, ko 각각
  tabItems: TabItem[];
  isMainVisualHidden: boolean;
  pName: string;
}

const ProductSectionTabTemplate = ({
  productName,
  productDescription,
  tabItems,
  isMainVisualHidden,
  pName,
}: Props) => {
  const { locale } = useRouter();
  const renderInformation = useMemo(
    () =>
      productName === 'DeepField'
        ? deepfieldContents[pName]
        : pulsarContents[pName],
    [pName, productName]
  );
  const [targetRef, isVisible, entry] = useInView({
    fallbackInView: true,
    threshold: 0,
    rootMargin: '-72px',
  });

  return (
    <ProductSectionTabTemplateContainer>
      <div className="product-wrapper" ref={targetRef} id="product-section">
        <H2 as="h3" className="product-name">
          {productName}
        </H2>
        <SubtitleM className="rep-product-text">
          {locale === 'en' ? productDescription[1] : productDescription[0]}
        </SubtitleM>
      </div>
      <ProductTabMenu
        isFloatTab={
          typeof window !== 'undefined' &&
          !isVisible &&
          !!entry?.target &&
          isMainVisualHidden
        }
        tabItems={tabItems}
      />
      {typeof window !== 'undefined' &&
        !isVisible &&
        !!entry?.target &&
        isMainVisualHidden && <div className="floating-space-wrapper" />}

      <ProductBanner
        bannerImg={renderInformation?.banner?.bannerImg.src}
        bannerText={renderInformation?.banner?.bannerText[locale]}
        bannerTitle={renderInformation?.banner?.bannerTitle}
        buttonText={locale === 'en' ? 'Contact' : '제품 문의하기'}
      />

      <div className="layout-container">
        <ProductDescription
          img={renderInformation?.productDescription?.productDescriptionImg.src}
          descriptionTitle={
            renderInformation?.productDescription?.productTitle[locale]
          }
          descriptions={
            renderInformation?.productDescription?.productText[locale]
          }
          buttons={ProductTemplateButtons[pName]}
        />

        {/* Product Feature */}
        {!pName.toLowerCase().includes('avif') ? (
          <ProductFeatures
            pName={pName as TabItem['pathname']}
            locale={locale}
            featureItems={renderInformation?.productFeatures}
          />
        ) : (
          <AvifProductFeatures
            locale={locale}
            featureItems={renderInformation?.productFeatures}
          />
        )}

        {/* Product Provide */}
        <ProductProvide
          pName={pName}
          locale={locale}
          provide={renderInformation?.productProvide}
        />
        {!pName.toLowerCase().includes('avif') && (
          <ProductDevicesPlatforms
            locale={locale}
            devicesPlatformsDesc={renderInformation?.devicesPlatformsDesc}
            devicesPlatforms={renderInformation?.devicesPlatforms}
          />
        )}
      </div>

      <ProductContactUs locale={locale} />
      <Footer />
    </ProductSectionTabTemplateContainer>
  );
};

export default memo(ProductSectionTabTemplate);

const ProductSectionTabTemplateContainer = styled.main`
  display: flex;
  align-items: center;
  flex-direction: column;
  position: relative;
  height: 100%;

  .product-name {
    margin-top: 146px;
    margin-bottom: 16px;
    animation: ${txtAnimation} 1s cubic-bezier(0.4, 0, 0.2, 1) backwards;
  }
  .rep-product-text {
    color: ${GRAY60};
    max-width: 1200px;
    text-align: center;
    animation: ${txtAnimation} 1s 0.5s cubic-bezier(0.4, 0, 0.2, 1) backwards;
  }
  .floating-space-wrapper {
    height: 72px;
    @media screen and (min-width: 905px) {
      height: 60px;
    }
    @media screen and (max-width: 904px) and (min-width: 600px) {
      height: 52px;
    }
    @media screen and (max-width: 599px) {
      height: 44px;
    }
  }
  .layout-container {
    ${gridSystemMargin};
    max-width: 1200px;
  }
  .product-wrapper {
    display: flex;
    align-items: center;
    flex-direction: column;
    padding-right: 16px;
    padding-left: 16px;
    padding-bottom: 96px;
  }
  @media screen and (max-width: 1239px) {
    .product-name {
      margin-top: 96px;
    }
    .product-wrapper {
      padding-bottom: 56px;
    }
  }
  @media screen and (max-width: 904px) {
    .product-name {
      margin-top: 56px;
    }
    .product-wrapper {
      padding-bottom: 32px;
    }
  }
`;
